import { ChangeDetectionStrategy, Component, HostListener, computed, inject, signal } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { RouterModule } from "@angular/router";
import { ActivatedRoute } from "@angular/router";
import { CurrencyPipe, DatePipe, DecimalPipe } from "@angular/common";
import { rxResource } from "@angular/core/rxjs-interop";
import {
  Observable,
  catchError,
  forkJoin,
  map,
  of,
  switchMap,
  tap,
} from "rxjs";
import { environment } from "../../environments/environment";
import { ApiService } from "../core/api.service";
import { EstimateShareService } from "../core/estimate-share.service";
import { ConfirmDialogService } from "../core/confirm-dialog.service";
import {
  Customer,
  Room,
  WindowEntity,
  Estimate,
  EstimateItem,
  Settings,
  AdditionalCost,
  Measurement,
  CatalogBlind,
  EstimateVersion,
} from "../core/models";
import {
  calculateOrderCost,
  OrderCostBreakdown,
  RoomCostInput,
  ControlType,
} from "../core/pricing.util";

interface LoadedPage {
  customer: Customer | null;
  rooms: Room[];
  windows: WindowEntity[];
  measurementsByWindow: Map<string, Measurement | null>;
  estimate: Estimate;
  terms: string[];
  catalog: CatalogBlind[];
  /** True when the estimate came from the server (a saved one). */
  wasSaved: boolean;
}

@Component({
  selector: "app-estimate-page",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, RouterModule, CurrencyPipe, DatePipe, DecimalPipe],
  templateUrl: "./estimate-page.component.html",
  styleUrls: ["./estimate-page.component.scss"],
})
export class EstimatePageComponent {
  private readonly api = inject(ApiService);
  private readonly route = inject(ActivatedRoute);
  private readonly share = inject(EstimateShareService);
  private readonly confirmDialog = inject(ConfirmDialogService);

  readonly customerId: string;
  discountMode: "amount" | "percent" = "amount";
  discountPercent = 0;
  isDirty = false;

  @HostListener("window:beforeunload", ["$event"])
  onBeforeUnload(event: BeforeUnloadEvent) {
    if (this.isDirty) {
      event.preventDefault();
    }
  }

  markDirty() {
    this.isDirty = true;
  }

  manufacturingCost = 0;
  additionalCosts: AdditionalCost[] = [];

  /** Auto-computed breakdown — recalculated whenever measurements are loaded */
  calculatedBreakdown: OrderCostBreakdown | null = null;

  readonly loading = signal(false);
  readonly saving = signal(false);
  readonly message = signal("");
  readonly supplierSaving = signal(false);
  readonly shareCopied = signal(false);

  readonly customer = signal<Customer | null>(null);
  readonly suppliers = signal<{ id: string; name: string }[]>([]);
  selectedSupplierId = "";
  readonly rooms = signal<Room[]>([]);
  readonly windows = signal<WindowEntity[]>([]);
  readonly estimate = signal<Estimate | null>(null);
  readonly terms = signal<string[]>([]);
  readonly versions = signal<EstimateVersion[]>([]);
  readonly viewingVersion = signal<number | null>(null);

  selectedWindowIds = signal<string[]>([]);
  private rateOverrides = new Map<string, number>();

  private pageContext: {
    rooms: Room[];
    windows: WindowEntity[];
    measurementsByWindow: Map<string, Measurement | null>;
    catalog: CatalogBlind[];
  } | null = null;

  private readonly pageResource = rxResource({
    params: () => this.customerId,
    stream: ({ params }) => this.loadPage(params),
  });

  get totalAdditionalCosts(): number {
    return this.additionalCosts.reduce(
      (sum, c) => sum + (Number(c.amount) || 0),
      0,
    );
  }

  get profit(): number {
    const total = this.estimate()?.total ?? 0;
    return (
      total - (Number(this.manufacturingCost) || 0) - this.totalAdditionalCosts
    );
  }

  addAdditionalCost() {
    this.additionalCosts.push({ label: "", amount: 0 });
    this.markDirty();
  }

  removeAdditionalCost(index: number) {
    this.additionalCosts.splice(index, 1);
    this.markDirty();
  }

  get totalRooms(): number {
    return this.rooms().length;
  }

  get totalBlinds(): number {
    return this.windows().length;
  }

  get estimateExpiryDate(): Date | null {
    const estimate = this.estimate();
    if (!estimate) return null;
    const createdDate = new Date(estimate.createdAt);
    const expiryDate = new Date(createdDate);
    expiryDate.setDate(expiryDate.getDate() + 10);
    return expiryDate;
  }

  constructor() {
    this.customerId = this.route.snapshot.paramMap.get("customerId") || "";
  }

  /**
   * Loads the entire page through a single reactive pipeline so later loads
   * are chained off earlier ones (no parallel/wild subscriptions to race).
   */
  private loadPage(customerId: string): Observable<LoadedPage> {
    return forkJoin({
      customer: this.api
        .getCustomer(customerId)
        .pipe(catchError(() => of(null as Customer | null))),
      rooms: this.api
        .getRooms(customerId)
        .pipe(catchError(() => of([] as Room[]))),
      settings: this.api.getSettings().pipe(catchError(() => of(null))),
      catalog: this.api
        .getCatalogBlinds()
        .pipe(catchError(() => of([] as CatalogBlind[]))),
    }).pipe(
      switchMap(({ customer, rooms, settings, catalog }) =>
        this.loadWindowsWithMeasurements(rooms).pipe(
          map(({ windows, measurementsByWindow }) => ({
            customer,
            rooms,
            settings,
            catalog,
            windows,
            measurementsByWindow,
          })),
        ),
      ),
      switchMap(
        ({ customer, rooms, windows, measurementsByWindow, settings, catalog }) =>
          this.api.getEstimate(customerId).pipe(
            map((savedEstimate) => ({
              customer,
              rooms,
              windows,
              measurementsByWindow,
              settings,
              catalog,
              terms: settings?.terms ?? [],
              estimate: this.normalizeSavedEstimate(savedEstimate),
              wasSaved: true,
            })),
            catchError(() => {
              // No saved estimate — generate fresh and auto-populate mfg cost
              const fresh = this.buildEstimate(
                rooms,
                windows,
                measurementsByWindow,
                catalog,
              );
              return of({
                customer,
                rooms,
                windows,
                measurementsByWindow,
                settings,
                catalog,
                terms: settings?.terms ?? [],
                estimate: fresh,
                wasSaved: false,
              });
            }),
          ),
      ),
      tap((page) => this.applyLoadedPage(page)),
      map((page) => page),
    );
  }

  /** Loads windows for every room and the latest measurement per window. */
  private loadWindowsWithMeasurements(rooms: Room[]) {
    if (!rooms.length) {
      return of({ windows: [] as WindowEntity[], measurementsByWindow: new Map<string, Measurement | null>() });
    }
    // One aggregate request fetches windows across all rooms, avoiding the
    // N parallel calls that previously tripped Lambda throttling (503s).
    return this.api.getCustomerWindows(this.customerId).pipe(
      map((allWindows) => ({ windows: allWindows })),
      switchMap(({ windows }) => {
        const windowIds = windows.map((w) => w.id);
        if (!windowIds.length) {
          return of({
            windows,
            measurementsByWindow: new Map<string, Measurement | null>(),
          });
        }
        return this.api
          .getMeasurementsBulk(windowIds)
          .pipe(
            catchError(() => of([] as Measurement[])),
            map((allMeasurements) => {
              const latestByWindow = new Map<string, Measurement | null>();
              for (const m of allMeasurements) {
                const current = latestByWindow.get(m.windowId);
                if (!current || (m.createdAt || "") > (current.createdAt || "")) {
                  latestByWindow.set(m.windowId, m);
                }
              }
              const measurementsByWindow = new Map(
                windows.map((w) => [w.id, latestByWindow.get(w.id) ?? null]),
              );
              return { windows, measurementsByWindow };
            }),
          );
      }),
    );
  }

  private normalizeSavedEstimate(savedEstimate: Estimate): Estimate {
    return {
      ...savedEstimate,
      discountLabel: savedEstimate.discountLabel ?? "Total Discount",
      items: [...savedEstimate.items].sort((a, b) => {
        const roomCmp = a.roomName.localeCompare(b.roomName, undefined, {
          sensitivity: "base",
        });
        if (roomCmp !== 0) return roomCmp;
        return (a.windowLabel ?? "").localeCompare(
          b.windowLabel ?? "",
          undefined,
          { sensitivity: "base" },
        );
      }),
    };
  }

  private applyLoadedPage(page: LoadedPage) {
    this.customer.set(page.customer);
    this.rooms.set(page.rooms);
    this.windows.set(page.windows);
    this.estimate.set(page.estimate);
    this.terms.set(page.terms);
    this.selectedSupplierId = page.customer?.supplierId ?? "";
    this.loadSuppliers();

    this.pageContext = {
      rooms: page.rooms,
      windows: page.windows,
      measurementsByWindow: page.measurementsByWindow,
      catalog: page.catalog,
    };

    // Window selection: restore from saved estimate, else default to all windows.
    const savedWindowIds = page.estimate.windowIds;
    this.selectedWindowIds.set(
      savedWindowIds ? savedWindowIds : page.windows.map((w) => w.id),
    );

    // Preserve manual rate edits across rebuilds (match by windowId when present).
    this.rateOverrides.clear();
    for (const item of page.estimate.items) {
      const windowId =
        item.windowId ?? this.findWindowId(item, page.rooms, page.windows);
      if (windowId) this.rateOverrides.set(windowId, item.rate);
    }

    this.calculatedBreakdown = this.computeBreakdown(
      page.rooms,
      page.windows,
      page.measurementsByWindow,
    );
    const savedEstimate = page.estimate;
    if (page.wasSaved) {
      this.manufacturingCost = savedEstimate.manufacturingCost ?? 0;
      this.additionalCosts = savedEstimate.additionalCosts
        ? [...savedEstimate.additionalCosts]
        : [];
    } else {
      // Freshly generated estimate — auto-populate mfg cost without dirtying.
      this.additionalCosts = [];
      if (this.calculatedBreakdown) {
        this.manufacturingCost =
          Math.round(this.calculatedBreakdown.total * 100) / 100;
      }
    }
    this.initializeDiscountState(savedEstimate);
    this.recalculateTotal();
    this.loadVersions();

    this.isDirty = false;
    this.loading.set(false);
  }

  private loadVersions() {
    this.api.getEstimateVersions(this.customerId).subscribe({
      next: (versions) => this.versions.set(versions),
      error: () => this.versions.set([]),
    });
  }

  loadSuppliers() {
    this.api.listSuppliers().subscribe({
      next: (suppliers) => {
        this.suppliers.set(suppliers);
        if (!this.selectedSupplierId) {
          this.selectedSupplierId = this.customer()?.supplierId ?? "";
        }
      },
      error: () => this.suppliers.set([]),
    });
  }

  assignSupplier() {
    if (!this.selectedSupplierId) return;
    this.supplierSaving.set(true);
    this.api
      .assignSupplier(this.customerId, this.selectedSupplierId)
      .subscribe({
        next: (customer) => {
          this.customer.set(customer);
          this.supplierSaving.set(false);
          this.message.set("Supplier assigned.");
        },
        error: (err) => {
          this.message.set(
            err?.error?.message ?? "Failed to assign supplier.",
          );
          this.supplierSaving.set(false);
        },
      });
  }

  /** Assigned suppliers with their own quote + status (per-window routing). */
  readonly assignedSupplierRows = computed(() => {
    const c = this.customer();
    if (!c) return [] as {
      id: string;
      name: string;
      status: string;
      quote?: { url: string; name: string; amount?: number; uploadedAt: string };
    }[];
    const ids = c.supplierIds?.length
      ? c.supplierIds
      : c.supplierId
        ? [c.supplierId]
        : [];
    const nameById = new Map(this.suppliers().map((s) => [s.id, s.name]));
    return ids.map((id) => ({
      id,
      name: nameById.get(id) ?? id,
      status:
        c.supplierQuoteStatuses?.[id] ??
        (c.supplierId === id ? c.supplierQuoteStatus : undefined) ??
        "pending",
      quote:
        c.supplierQuotes?.[id] ??
        (c.supplierId === id ? c.supplierQuote : undefined),
    }));
  });

  approveQuote(supplierId?: string) {
    this.supplierSaving.set(true);
    this.api.approveSupplierQuote(this.customerId, supplierId).subscribe({
      next: () => {
        this.reloadCustomer();
        this.reloadEstimate();
        this.message.set("Quote approved.");
      },
      error: (err) => {
        this.message.set(err?.error?.message ?? "Failed to approve quote.");
        this.supplierSaving.set(false);
      },
    });
  }

  rejectQuote(supplierId?: string) {
    this.supplierSaving.set(true);
    this.api.rejectSupplierQuote(this.customerId, supplierId).subscribe({
      next: () => {
        this.reloadCustomer();
        this.reloadEstimate();
        this.message.set("Quote rejected.");
      },
      error: (err) => {
        this.message.set(err?.error?.message ?? "Failed to reject quote.");
        this.supplierSaving.set(false);
      },
    });
  }

  /** Resolves the supplier portal base URL: runtime-config override wins over the environment value. */
  supplierPortalUrl(): string {
    const override = (window as unknown as { __SUPPLIER_PORTAL_URL__?: string })
      .__SUPPLIER_PORTAL_URL__;
    return (override || environment.supplierPortalUrl).replace(/\/+$/, "");
  }

  async copyShareLink() {
    this.shareCopied.set(false);
    const link = `${this.supplierPortalUrl()}/estimates/${this.customerId}`;
    try {
      await navigator.clipboard.writeText(link);
      this.shareCopied.set(true);
    } catch {
      this.message.set("Failed to copy link to clipboard.");
    }
  }

  private reloadCustomer() {
    this.api.getCustomer(this.customerId).subscribe({
      next: (customer) => {
        this.customer.set(customer);
        this.supplierSaving.set(false);
      },
      error: () => this.supplierSaving.set(false),
    });
  }

  /**
   * Approval saves a new estimate version whose manufacturing cost is the
   * supplier's quoted amount, so re-fetch it to keep the cost/version UI current.
   */
  private reloadEstimate() {
    this.api.getEstimate(this.customerId).subscribe({
      next: (estimate) => {
        const normalized = this.normalizeSavedEstimate(estimate);
        this.estimate.set(normalized);
        this.manufacturingCost = normalized.manufacturingCost ?? 0;
        this.additionalCosts = normalized.additionalCosts
          ? [...normalized.additionalCosts]
          : [];
        this.loadVersions();
      },
      error: () => {},
    });
  }

  /** Resolves a window id for a saved item, falling back to room-name + window-label match for legacy estimates. */
  private findWindowId(
    item: EstimateItem,
    rooms: Room[],
    windows: WindowEntity[],
  ): string | undefined {
    const room = rooms.find((r) => r.name === item.roomName);
    if (!room) return undefined;
    const window = windows.find(
      (w) => w.roomId === room.id && w.label === item.windowLabel,
    );
    return window?.id;
  }

  private computeBreakdown(
    rooms: Room[],
    windows: WindowEntity[],
    measurementsByWindow: Map<string, Measurement | null>,
  ): OrderCostBreakdown | null {
    const roomInputs: RoomCostInput[] = rooms.map((room) => {
      const roomWindows = windows.filter((w) => w.roomId === room.id);
      return {
        blinds: roomWindows
          .map((w) => {
            const m = measurementsByWindow.get(w.id) ?? null;
            if (!m || !w.style?.trim()) return null;
            const widthIn = m.unit === "cm" ? m.width / 2.54 : m.width;
            const heightIn = m.unit === "cm" ? m.height / 2.54 : m.height;
            return {
              widthIn,
              heightIn,
              fabricCode: w.style,
              controlType: (w.controlType ?? "motorized") as ControlType,
              hasSolarPanel: w.hasSolarPanel ?? false,
            };
          })
          .filter((b): b is NonNullable<typeof b> => b !== null),
      };
    });

    const hasAnyBlind = roomInputs.some((r) => r.blinds.length > 0);
    return hasAnyBlind ? calculateOrderCost(roomInputs) : null;
  }

  /** Apply the calculated breakdown to manufacturingCost (overwrites current value) */
  applyCalculatedCost() {
    if (!this.calculatedBreakdown) return;
    this.manufacturingCost =
      Math.round(this.calculatedBreakdown.total * 100) / 100;
    this.markDirty();
  }

  /** Rebuilds the estimate items from the currently selected windows, preserving manual rate edits. */
  private rebuildItems() {
    const estimate = this.estimate();
    if (!estimate || !this.pageContext) return;
    const { rooms, windows, measurementsByWindow, catalog } = this.pageContext;

    const items = this.buildItemsFromWindows(
      rooms,
      windows,
      measurementsByWindow,
      catalog,
      this.selectedWindowIds(),
    );
    estimate.items = items;
    this.recalculateTotal();
  }

  private buildEstimate(
    rooms: Room[],
    windows: WindowEntity[],
    measurementsByWindow: Map<string, Measurement | null>,
    catalog: CatalogBlind[],
  ): Estimate {
    const items = this.buildItemsFromWindows(
      rooms,
      windows,
      measurementsByWindow,
      catalog,
      windows.map((w) => w.id),
    );
    const subtotal = items.reduce((s, i) => s + i.amount, 0);
    return {
      id: `EST-${Math.random().toString(36).substr(2, 9).toUpperCase()}`,
      customerId: this.customerId,
      items,
      windowIds: windows.map((w) => w.id),
      subtotal,
      totalDiscount: 0,
      total: subtotal,
      discountLabel: "Total Discount",
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
  }

  private buildItemsFromWindows(
    rooms: Room[],
    windows: WindowEntity[],
    measurementsByWindow: Map<string, Measurement | null>,
    catalog: CatalogBlind[],
    selectedWindowIds: string[],
  ): EstimateItem[] {
    const items: EstimateItem[] = [];
    const selected = new Set(selectedWindowIds);
    const typeByStyle = new Map<string, "semi-blackout" | "blackout">();
    for (const b of catalog) {
      if (b.semiBlackoutCode) typeByStyle.set(b.semiBlackoutCode, "semi-blackout");
      if (b.blackoutCode) typeByStyle.set(b.blackoutCode, "blackout");
    }

    const basePriceFor = (window: WindowEntity): number => {
      if (window.price && window.price > 0) {
        return window.price;
      }
      const measurement = measurementsByWindow.get(window.id) ?? null;
      const widthIn = measurement
        ? measurement.unit === "cm"
          ? measurement.width / 2.54
          : measurement.width
        : undefined;
      if (widthIn !== undefined && widthIn < 20) {
        return 170;
      }
      if (widthIn !== undefined && widthIn > 50) {
        return 300;
      }
      return 250;
    };

    const sortedRooms = [...rooms].sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: "base" }),
    );
    sortedRooms.forEach((room) => {
      const roomWindows = [...windows.filter((w) => w.roomId === room.id)].sort(
        (a, b) =>
          (a.label ?? "").localeCompare(b.label ?? "", undefined, {
            sensitivity: "base",
          }),
      );
      roomWindows.forEach((window) => {
        if (!selected.has(window.id)) return;
        const baseRate = basePriceFor(window);
        const motorizedSurcharge =
          window.controlType === "motorized" ? 70 : 0;
        const solarSurcharge = window.hasSolarPanel ? 40 : 0;
        const rate =
          this.rateOverrides.get(window.id) ??
          baseRate + motorizedSurcharge + solarSurcharge;
        const quantity = 1;
        const discount = 0;
        const amount = rate * quantity;

        items.push({
          windowId: window.id,
          roomName: room.name,
          windowLabel: window.label,
          quantity,
          rate,
          discount,
          discountPercent: 0,
          amount,
          style: window.style,
          type: window.style
            ? typeByStyle.get(window.style)
            : undefined,
          controlType: window.controlType,
          hasSolarPanel: window.hasSolarPanel,
        });
      });
    });

    return items;
  }

  windowsByRoom(roomId: string): WindowEntity[] {
    return this.windows().filter((w) => w.roomId === roomId);
  }

  toggleWindow(windowId: string) {
    const ids = new Set(this.selectedWindowIds());
    if (ids.has(windowId)) {
      ids.delete(windowId);
    } else {
      ids.add(windowId);
    }
    this.selectedWindowIds.set([...ids]);
    this.markDirty();
    this.rebuildItems();
  }

  viewVersion(version: number) {
    if (this.isDirty) {
      this.message.set(
        "Save or discard your changes before viewing a past revision.",
      );
      return;
    }
    this.api.getEstimateVersion(this.customerId, version).subscribe({
      next: (estimate) => {
        this.estimate.set(estimate);
        this.viewingVersion.set(version);
        this.isDirty = false;
      },
      error: () => this.message.set("Failed to load estimate version."),
    });
  }

  backToLatest() {
    if (this.isDirty) {
      this.message.set(
        "Save or discard your changes before leaving a past revision.",
      );
      return;
    }
    this.api.getEstimate(this.customerId).subscribe({
      next: (estimate) => {
        this.estimate.set(estimate);
        this.viewingVersion.set(null);
        this.isDirty = false;
      },
      error: () => this.message.set("Failed to load latest estimate."),
    });
  }

  updatePrice(index: number, newPrice: number) {
    const estimate = this.estimate();
    if (!estimate || !estimate.items[index]) return;

    const item = estimate.items[index];
    item.rate = newPrice;
    item.amount = newPrice * item.quantity;
    if (item.windowId) this.rateOverrides.set(item.windowId, newPrice);

    this.markDirty();
    this.recalculateTotal();
  }

  setDiscountMode(mode: "amount" | "percent") {
    if (this.discountMode === mode) return;
    this.discountMode = mode;
    const estimate = this.estimate();
    if (!estimate) return;

    if (mode === "percent") {
      this.discountPercent = this.calculateDiscountPercent(
        estimate.totalDiscount,
        estimate.subtotal,
      );
    }

    this.markDirty();
    this.recalculateTotal();
  }

  onDiscountLabelChange() {
    this.markDirty();
    // Persist a fresh estimate reference so the label re-renders.
    const estimate = this.estimate();
    if (estimate) {
      this.estimate.set({ ...estimate });
    }
  }

  updateTotalDiscountAmount(newDiscount: number) {
    const estimate = this.estimate();
    if (!estimate) return;
    estimate.totalDiscount = Math.max(0, Number(newDiscount) || 0);
    this.discountMode = "amount";
    this.markDirty();
    this.recalculateTotal();
  }

  updateTotalDiscountPercent(newPercent: number) {
    const estimate = this.estimate();
    if (!estimate) return;
    this.discountPercent = Math.max(0, Math.min(100, Number(newPercent) || 0));
    this.discountMode = "percent";
    this.markDirty();
    this.recalculateTotal();
  }

  recalculateTotal() {
    const estimate = this.estimate();
    if (!estimate) return;

    estimate.subtotal = 0;
    const discountPercent =
      this.discountMode === "percent" ? this.discountPercent : 0;

    estimate.items.forEach((item) => {
      const lineAmount = item.rate * item.quantity;
      item.amount = lineAmount;
      estimate.subtotal += lineAmount;
    });

    if (this.discountMode === "percent") {
      const normalizedPercent = Math.max(
        0,
        Math.min(100, Number(discountPercent) || 0),
      );
      this.discountPercent = normalizedPercent;
      estimate.totalDiscount = (estimate.subtotal * normalizedPercent) / 100;
    } else {
      const totalDiscount = Math.max(0, Number(estimate.totalDiscount) || 0);
      estimate.totalDiscount = Math.min(totalDiscount, estimate.subtotal);
    }

    estimate.total = estimate.subtotal - estimate.totalDiscount;
    // Emit a fresh reference so OnPush re-renders the totals.
    this.estimate.set({ ...estimate });
  }

  private initializeDiscountState(estimate: Estimate) {
    this.discountMode = "amount";
    this.discountPercent = this.calculateDiscountPercent(
      estimate.totalDiscount,
      estimate.subtotal,
    );
  }

  private calculateDiscountPercent(totalDiscount: number, subtotal: number) {
    if (subtotal <= 0) return 0;
    return Math.max(0, Math.min(100, (totalDiscount / subtotal) * 100));
  }

  saveEstimate() {
    const estimate = this.estimate();
    if (!estimate) return;

    if (this.viewingVersion() !== null) {
      this.message.set("Viewing a past revision — switch to latest to edit.");
      return;
    }

    estimate.manufacturingCost = Number(this.manufacturingCost) || 0;
    estimate.additionalCosts = this.additionalCosts.map((c) => ({
      label: c.label,
      amount: Number(c.amount) || 0,
    }));

    estimate.windowIds = this.selectedWindowIds();

    this.saving.set(true);
    this.api.saveEstimate(this.customerId, estimate).subscribe({
      next: (saved) => {
        this.estimate.set(saved);
        this.viewingVersion.set(null);
        this.isDirty = false;
        this.message.set("Estimate saved successfully.");
        this.loadVersions();
        this.saving.set(false);
      },
      error: (error) => {
        this.message.set("Failed to save estimate. Please try again.");
        this.saving.set(false);
        console.error("Error saving estimate:", error);
      },
    });
  }

  async downloadEstimate() {
    const element = document.getElementById("printable-estimate");
    if (!element) {
      console.error("Estimate element not found");
      return;
    }

    // Clone the estimate element
    const estimateClone = element.cloneNode(true) as HTMLElement;
    this.prepareEstimateForPrint(estimateClone);

    // Convert logo to base64 for embedding
    const logoImg = document.getElementById(
      "company-logo-img",
    ) as HTMLImageElement;
    let logoDataUrl = "";
    if (logoImg) {
      const canvas = document.createElement("canvas");
      canvas.width = logoImg.naturalWidth;
      canvas.height = logoImg.naturalHeight;
      const ctx = canvas.getContext("2d");
      if (ctx) {
        ctx.drawImage(logoImg, 0, 0);
        logoDataUrl = canvas.toDataURL("image/png");
      }
    }

    // Update logo src in clone
    const clonedLogo = estimateClone.querySelector(
      ".company-logo",
    ) as HTMLImageElement;
    if (clonedLogo && logoDataUrl) {
      clonedLogo.src = logoDataUrl;
    }

    // Create the HTML content
    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>${this.estimate()?.id || "Estimate"}.pdf</title>
        <style>
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          body {
            font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
            line-height: 1.6;
            color: #111827;
            background: white;
            padding: 40px;
          }
          .estimate-container {
            max-width: 900px;
            margin: 0 auto;
            background: white;
          }
          .estimate-header {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 40px;
            margin-bottom: 40px;
            padding-bottom: 24px;
            border-bottom: 2px solid rgba(15, 23, 42, 0.08);
          }
          .company-info {
            display: flex;
            gap: 16px;
            align-items: flex-start;
          }
          .company-logo {
            width: 56px;
            height: 56px;
            object-fit: contain;
          }
          .company-details h2 {
            font-size: 24px;
            margin: 0 0 8px;
            font-family: Impact, sans-serif;
          }
          .company-details h2 .big-g {
            color: #664107;
          }
          .company-details h2 .blinds {
            color: #f5ad45;
          }
          .company-details p {
            margin: 4px 0;
            font-size: 14px;
            color: #475569;
          }
          .estimate-title {
            text-align: right;
          }
          .estimate-title h1 {
            font-size: 40px;
            letter-spacing: 4px;
            margin: 0;
            color: #111827;
          }
          .estimate-title p {
            font-size: 14px;
            color: #475569;
            margin: 8px 0 0;
          }
          .billing-section {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 40px;
            margin-bottom: 32px;
          }
          .bill-to h3,
          .dates .date-item strong {
            font-size: 14px;
            font-weight: 600;
            color: #111827;
            margin-bottom: 8px;
          }
          .bill-to p {
            font-size: 14px;
            color: #475569;
            margin: 4px 0;
          }
          .summary-chips {
            display: flex;
            flex-wrap: wrap;
            gap: 8px;
            margin-top: 12px;
          }
          .summary-chip {
            display: inline-flex;
            align-items: center;
            background: rgba(15, 23, 42, 0.06);
            color: #111827;
            padding: 6px 10px;
            border-radius: 999px;
            font-size: 12px;
            font-weight: 600;
          }
          .date-item {
            margin-bottom: 16px;
          }
          .date-item p {
            font-size: 14px;
            color: #475569;
            margin: 4px 0;
          }
          table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 32px;
          }
          th {
            background: #111827;
            color: #ffffff;
            padding: 12px;
            text-align: left;
            font-weight: 600;
            font-size: 13px;
          }
          td {
            padding: 16px 12px;
            border-bottom: 1px solid rgba(15, 23, 42, 0.08);
            font-size: 14px;
            color: #111827;
          }
          .item-desc {
            display: flex;
            flex-direction: column;
          }
          .item-room {
            font-weight: 600;
            color: #111827;
          }
          .item-window {
            font-size: 12px;
            color: #475569;
          }
          .item-badges {
            display: flex;
            flex-wrap: wrap;
            gap: 4px;
          }
          .item-badge {
            display: inline-block;
            padding: 1px 8px;
            border-radius: 999px;
            font-size: 11px;
            font-weight: 600;
            white-space: nowrap;
          }
          .badge-motorized {
            background: #fef3c7;
            color: #b45309;
          }
          .badge-cordless {
            background: #e0f7f0;
            color: #047857;
          }
          .badge-manual {
            background: #f1f5f9;
            color: #334155;
          }
          .badge-solar {
            background: #e1f0ff;
            color: #1d4ed8;
          }
          .estimate-style {
            font-family: monospace;
            font-size: 13px;
            border-radius: 6px;
            padding: 2px 6px;
            white-space: nowrap;
            border: 1px solid rgba(15, 23, 42, 0.12);
          }
          .estimate-style.type-transparent {
            background: #ffffff;
            color: #111827;
          }
          .estimate-style.type-semi {
            background: #e5e7eb;
            color: #111827;
          }
          .estimate-style.type-blackout {
            background: #111827;
            color: #ffffff;
          }
          .muted {
            color: #64748b;
          }
          .discount-toggle,
          .toggle-button {
            display: none;
          }
          .totals-section {
            display: flex;
            justify-content: flex-end;
            margin-bottom: 32px;
          }
          .totals-grid {
            display: grid;
            gap: 0;
            min-width: 300px;
          }
          .total-row {
            display: grid;
            grid-template-columns: 1fr 150px;
            gap: 24px;
            padding: 12px 0;
            border-bottom: 1px solid rgba(15, 23, 42, 0.08);
            font-size: 14px;
          }
          .total-row span:last-child {
            text-align: right;
            font-weight: 600;
          }
          .total-row.highlight {
            background: #f8faff;
            padding: 12px;
            margin: 0 -12px;
          }
          .total-row.grand-total {
            border: none;
            border-top: 2px solid rgba(15, 23, 42, 0.12);
            padding-top: 16px;
            font-size: 16px;
            font-weight: 700;
            color: #111827;
          }
          .terms-section {
            margin-top: 32px;
          }
          .terms-section h3 {
            font-size: 16px;
            font-weight: 700;
            margin-bottom: 12px;
            color: #111827;
          }
          .terms-section ul {
            list-style-position: outside;
            padding-left: 20px;
          }
          .terms-section li {
            margin-bottom: 8px;
            font-size: 13px;
            color: #475569;
            line-height: 1.6;
          }
          @media print {
            body {
              padding: 0;
            }
          }
        </style>
      </head>
      <body>
        ${estimateClone.innerHTML}
      </body>
      </html>
    `;

    // Native (Capacitor): hand the HTML to the iOS share sheet so the user
    // can Save to Files / AirDrop / Message. Web falls through to print.
    const shared = await this.share.shareHtml("Estimate", htmlContent);
    if (shared) return;

    // Create a new window for printing
    const printWindow = window.open("", "", "width=900,height=700");
    if (!printWindow) {
      await this.confirmDialog.alert({
        title: "Pop-ups blocked",
        message: "Please allow pop-ups to download the estimate.",
      });
      return;
    }
    printWindow.document.write(htmlContent);
    printWindow.document.close();

    // Wait for content to load then print
    setTimeout(() => {
      printWindow.print();
    }, 250);
  }

  private prepareEstimateForPrint(container: HTMLElement) {
    const inputs = Array.from(container.querySelectorAll("input"));
    const currencyFormatter = new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    });

    inputs.forEach((input) => {
      const span = document.createElement("span");
      const value = input.value;
      const isDiscountField = input.classList.contains("discount-input");
      if (isDiscountField) {
        if (this.discountMode === "percent") {
          const percentValue = this.discountPercent ?? (Number(value) || 0);
          span.textContent = `${percentValue}%`;
        } else {
          const discountValue =
            this.estimate()?.totalDiscount ?? (Number(value) || 0);
          span.textContent = currencyFormatter.format(discountValue);
        }
      } else {
        span.textContent = value;
      }
      span.className = "print-value";
      input.replaceWith(span);
    });
  }
}
